import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';

const CreateReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportTypes, setReportTypes] = useState([
    { id: 'statistical', name: 'Statistical Analysis', icon: 'chart-bar', description: 'Detailed statistical analysis of all health metrics' },
    { id: 'summary', name: 'Summary Report', icon: 'file-alt', description: 'High-level overview of key findings' },
    { id: 'analysis', name: 'Detailed Analysis', icon: 'microscope', description: 'In-depth analysis of health patterns and correlations' },
    { id: 'dashboard', name: 'Dashboard Report', icon: 'tachometer-alt', description: 'Visual dashboard summary for presentation' }
  ]);
  const [selectedType, setSelectedType] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    filters: {
      dateRange: '30days',
      minScore: '',
      maxScore: '',
      ageGroup: '',
      gender: ''
    }
  });
  const [savedReports, setSavedReports] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSavedReports();
  }, []);

  const loadSavedReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/reports');
      if (response.data.success) {
        setSavedReports(response.data.reports || []);
      }
    } catch (err) {
      console.error("Failed to load saved reports", err);
      setError("Failed to load saved reports");
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setReportData(null);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      filters: {
        ...formData.filters,
        [name]: value
      }
    });
  };

  const generateReport = async () => {
    if (!selectedType) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const response = await api.post('/reports/generate', {
        report_type: selectedType.id,
        filters: formData.filters
      });
      
      if (response.data.success) {
        setReportData(response.data);
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (err) {
      console.error("Failed to generate report", err);
      setError("An error occurred while generating the report");
    } finally {
      setGenerating(false);
    }
  };

  const prepareSummaryReport = (formData) => {
    return {
      ...formData,
      data: {
        summary: {
          overview: formData.overview || formData.description,
          findings: formData.summary?.highlights || [],
          conclusions: formData.conclusions || formData.recommendations || [],
        }
      }
    };
  };

  const saveReport = async () => {
    if (!selectedType || !formData.title.trim()) return;
    
    setSaving(true);
    setError(null);
    
    try {
      let reportToSave = {
        title: formData.title,
        description: formData.description,
        report_type: selectedType.id,
        filters: formData.filters,
        data: reportData.data
      };
      
      if (selectedType.id === 'summary' && reportData.data) {
        reportToSave.data = {
          summary: {
            overview: reportData.data.summary?.overview || formData.description,
            findings: reportData.data.summary?.highlights || [],
            conclusions: reportData.data.summary?.conclusions || []
          }
        };
      }
      
      const response = await api.post('/reports', reportToSave);
      
      if (response.data.success) {
        setFormData({
          title: '',
          description: '',
          filters: {
            dateRange: '30days',
            minScore: '',
            maxScore: '',
            ageGroup: '',
            gender: ''
          }
        });
        setSelectedType(null);
        setReportData(null);
        loadSavedReports();
        alert("Report saved successfully!");
      } else {
        setError(response.data.message || "Failed to save report");
      }
    } catch (err) {
      console.error("Failed to save report", err);
      setError("An error occurred while saving the report");
    } finally {
      setSaving(false);
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;
    
    const data = reportData.data;
    
    switch (selectedType.id) {
      case 'statistical':
        return (
          <div className="report-preview">
            <h5 className="mb-3">Statistical Analysis</h5>
            {data.healthMetrics && (
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0">Health Metrics</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="stat-item">
                        <span className="stat-label">Average Score</span>
                        <div className="stat-value text-primary">{data.healthMetrics.averageScore.toFixed(1)}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stat-item">
                        <span className="stat-label">Median Score</span>
                        <div className="stat-value text-success">{data.healthMetrics.medianScore}</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="stat-item">
                        <span className="stat-label">Standard Deviation</span>
                        <div className="stat-value text-info">{data.healthMetrics.stdDeviation.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {data.demographicBreakdown && (
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0">Demographic Breakdown</h6>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Count</th>
                          <th>Avg. Score</th>
                          <th>% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.demographicBreakdown).map(([key, value]) => (
                          <tr key={key}>
                            <td>{key}</td>
                            <td>{value.count}</td>
                            <td>{value.averageScore.toFixed(1)}</td>
                            <td>{value.percentage.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'summary':
        return (
          <div className="report-preview">
            <h5 className="mb-3">Summary Report</h5>
            {data.summary && (
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0">Key Findings</h6>
                </div>
                <div className="card-body">
                  <p className="lead">{data.summary.overview}</p>
                  <hr />
                  <h6 className="text-primary">Highlights</h6>
                  <ul className="list-unstyled">
                    {data.summary.highlights.map((item, index) => (
                      <li key={index} className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {data.trends && (
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0">Trends</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    {Object.entries(data.trends).map(([key, value]) => (
                      <div key={key} className="col-md-4 mb-3">
                        <div className="card h-100 border-0 bg-light">
                          <div className="card-body text-center">
                            <h3 className={`mb-2 ${value.trend === 'up' ? 'text-success' : value.trend === 'down' ? 'text-danger' : 'text-warning'}`}>
                              <i className={`fas fa-arrow-${value.trend === 'up' ? 'up' : value.trend === 'down' ? 'down' : 'right'}`}></i>
                              {' '}{value.percentage}%
                            </h3>
                            <p className="mb-0 text-muted">{key}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <div className="report-preview">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Report generated successfully. Preview is in development.
            </div>
            <pre className="bg-light p-3 rounded">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <ResearchLayout pageTitle="Create Reports">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <h3 className="mb-2">Create Research Reports</h3>
                <p className="mb-0 opacity-75">
                  Generate detailed analysis reports from your health research data
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Report Builder - Left Column */}
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-file-medical-alt text-info me-2"></i>
                  Report Builder
                </h5>
              </div>
              <div className="card-body">
                {!selectedType ? (
                  <>
                    <h5 className="mb-4">Select Report Type</h5>
                    <div className="row">
                      {reportTypes.map(type => (
                        <div key={type.id} className="col-md-6 col-lg-6 mb-4">
                          <div 
                            className={`card h-100 cursor-pointer report-type-card`}
                            onClick={() => handleTypeSelect(type)}
                          >
                            <div className="card-body d-flex">
                              <div className="report-icon me-3">
                                <i className={`fas fa-${type.icon} fa-2x text-info`}></i>
                              </div>
                              <div>
                                <h6 className="mb-1">{type.name}</h6>
                                <p className="text-muted small mb-0">{type.description}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5>{selectedType.name}</h5>
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => handleTypeSelect(null)}
                      >
                        <i className="fas fa-chevron-left me-1"></i>
                        Change Type
                      </button>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Report Title</label>
                        <input
                          type="text"
                          className="form-control"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Enter report title"
                        />
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Date Range</label>
                        <select 
                          className="form-select" 
                          name="dateRange" 
                          value={formData.filters.dateRange}
                          onChange={handleFilterChange}
                        >
                          <option value="7days">Last 7 days</option>
                          <option value="30days">Last 30 days</option>
                          <option value="90days">Last 90 days</option>
                          <option value="1year">Last year</option>
                          <option value="all">All time</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Min Health Score</label>
                        <input
                          type="number"
                          className="form-control"
                          name="minScore"
                          min="0"
                          max="100"
                          value={formData.filters.minScore}
                          onChange={handleFilterChange}
                          placeholder="Minimum score"
                        />
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Max Health Score</label>
                        <input
                          type="number"
                          className="form-control"
                          name="maxScore"
                          min="0"
                          max="100"
                          value={formData.filters.maxScore}
                          onChange={handleFilterChange}
                          placeholder="Maximum score"
                        />
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Age Group</label>
                        <select 
                          className="form-select" 
                          name="ageGroup" 
                          value={formData.filters.ageGroup}
                          onChange={handleFilterChange}
                        >
                          <option value="">All ages</option>
                          <option value="18-30">18-30</option>
                          <option value="31-45">31-45</option>
                          <option value="46-60">46-60</option>
                          <option value="60+">60+</option>
                        </select>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Gender</label>
                        <select 
                          className="form-select" 
                          name="gender" 
                          value={formData.filters.gender}
                          onChange={handleFilterChange}
                        >
                          <option value="">All genders</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Report description (optional)"
                      ></textarea>
                    </div>
                    
                    {error && (
                      <div className="alert alert-danger mb-3">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                      </div>
                    )}
                    
                    <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                      <button 
                        className="btn btn-info text-white" 
                        onClick={generateReport}
                        disabled={generating}
                      >
                        {generating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-bolt me-2"></i>
                            Generate Report
                          </>
                        )}
                      </button>
                      
                      {reportData && (
                        <button 
                          className="btn btn-success" 
                          onClick={saveReport}
                          disabled={saving || !formData.title.trim()}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-2"></i>
                              Save Report
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </>
                )}
                
                {reportData && (
                  <div className="mt-4 pt-4 border-top">
                    <h5 className="mb-3">Report Preview</h5>
                    {renderReportContent()}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Recent Reports - Right Column */}
          <div className="col-lg-4 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="mb-0">
                  <i className="fas fa-history text-info me-2"></i>
                  Recent Reports
                </h5>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-info" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : savedReports.length > 0 ? (
                  <div className="list-group">
                    {savedReports.slice(0, 5).map(report => (
                      <a 
                        key={report.id} 
                        href={`/statistician/reports/${report.id}`} 
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 className="mb-1">{report.title}</h6>
                          <div className="d-flex align-items-center small text-muted">
                            <span className="badge bg-info me-2">
                              {report.report_type}
                            </span>
                            <span>
                              {new Date(report.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <i className="fas fa-chevron-right"></i>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="mb-1">No recent reports found</p>
                    <p className="text-muted small">
                      Generate new reports or check back later
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default CreateReportsPage;
